import { useState } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { employeesApi } from '@/api/employees.api';
import { Button } from '@/components/ui/Button';
import { notify } from '@/core/toast/notify';
import { showApiError } from '@/utils/apiError';

type Props = {
  employeeId: number;
  hasLogin: boolean;
  email?: string;
};

export function EmployeePasswordPanel({ employeeId, hasLogin, email }: Props) {
  const [loading, setLoading] = useState<string | null>(null);
  const [lastPassword, setLastPassword] = useState<string | null>(null);

  const run = async (action: string, fn: () => Promise<{ generatedPassword?: string }>) => {
    setLoading(action);
    try {
      const res = await fn();
      if (res.generatedPassword) {
        setLastPassword(res.generatedPassword);
      }
      notify.success('Password action completed');
    } catch (err) {
      showApiError(err, 'Password action failed');
    } finally {
      setLoading(null);
    }
  };

  if (!hasLogin) {
    return (
      <p className="text-sm text-slate-500">
        Assign email and app role to create a login before managing passwords.
      </p>
    );
  }

  return (
    <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950/40">
      <p className="mb-2 text-sm font-semibold text-amber-900 dark:text-amber-200">
        Password management (super admin only)
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          loading={loading === 'generate'}
          onClick={() =>
            run('generate', () =>
              employeesApi.generatePassword(employeeId, { forceChangeOnLogin: true }),
            )
          }
        >
          Generate password
        </Button>
        <Button
          variant="secondary"
          loading={loading === 'reset'}
          onClick={() =>
            run('reset', () =>
              employeesApi.resetPassword(employeeId, { forceChangeOnLogin: true }),
            )
          }
        >
          Reset password
        </Button>
        <Button
          variant="secondary"
          loading={loading === 'send'}
          disabled={!email}
          onClick={() =>
            run('send', () =>
              employeesApi.sendCredentials(employeeId, {
                forceChangeOnLogin: true,
                sendEmail: true,
              }),
            )
          }
        >
          Email credentials
        </Button>
        <Button
          variant="secondary"
          loading={loading === 'force'}
          onClick={() => employeesApi.forcePasswordChange(employeeId, true).then(() => notify.success('Force change enabled'))}
        >
          Force change on login
        </Button>
      </div>
      {lastPassword && (
        <div className="mt-3 flex items-center gap-2 rounded bg-white px-3 py-2 text-sm dark:bg-slate-900">
          <code className="flex-1">{lastPassword}</code>
          <button
            type="button"
            className="text-primary-600"
            onClick={() => {
              navigator.clipboard.writeText(lastPassword);
              notify.success('Copied');
            }}
          >
            <ContentCopyIcon fontSize="small" />
          </button>
        </div>
      )}
    </div>
  );
}
